import { ReactNode, useMemo } from "react";
import { COBIcon } from "@core/components/icon/cob";
import { PortfolioValuationIcon } from "@core/components/icon/portfolio-valuation";
import { CapitalGainsIcon } from "@core/components/icon/capital-gain";
import { IconContainer } from "@core/components/icon/container";
import { useRouter } from "@tanstack/react-router";
import { Client } from "creso-core/types";

export const REPORT_ITEMS = [
  {
    label: "Capital Gains",
    icon: <CapitalGainsIcon />,
    modal: "capital-gains",
  },
  {
    label: "Portfolio Valuation",
    icon: <PortfolioValuationIcon />,
    modal: "portfolio-valuation",
  },
  {
    label: "Change of Broker",
    icon: <COBIcon />,
    modal: "change-of-broker",
  },
] as const;

export type ReportItem = {
  label: string;
  icon: ReactNode;
  onClick: () => void;
};

type ReportsProps = {
  reports: ReportItem[];
  title?: string;
  showTitle?: boolean;
};

export function OverviewReports({ client }: { client: Client }) {
  const router = useRouter();

  const reports = useMemo(
    () =>
      REPORT_ITEMS.map((report) => ({
        label: report.label,
        icon: report.icon,
        onClick: () =>
          router.navigate({
            to: ".",
            search: {
              client_id: client.id,
              modal: report.modal,
            },
          }),
      })),
    [client.id, router]
  );

  return <Reports reports={reports} />;
}

export function Reports({
  reports,
  title = "Reports",
  showTitle = true,
}: ReportsProps) {
  if (!reports.length) return null;

  return (
    <div className="flex flex-col gap-4">
      {showTitle ? (
        <span className="text-base font-semibold">{title}</span>
      ) : null}
      <div className="flex gap-4 items-center">
        {reports.map((report) => (
          <button
            key={report.label}
            type="button"
            onClick={report.onClick}
            className="flex-1 hover:bg-accent bg-card active:translate-y-px flex text-sm items-center gap-4 p-4 rounded-lg"
          >
            <IconContainer>{report.icon}</IconContainer>
            {report.label}
          </button>
        ))}
      </div>
    </div>
  );
}
